import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Result, Button } from 'antd';
import deDE from 'antd/locale/de_DE';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import SearchInterface from './components/SearchInterface';
import CostEstimation from './components/CostEstimation/CostEstimation';
import AgentChat from './components/AgentChat/AgentChat';
import UserAdmin from './components/Admin/UserAdmin';

const AccessDenied = () => {
  const { logout } = useAuth();
  return (
    <Result
      status="403"
      title="Kein Zugriff"
      subTitle="Ihr Benutzerkonto hat keine Berechtigung für diesen Bereich. Bitte wenden Sie sich an Ihren Administrator."
      extra={
        <Button type="primary" onClick={logout}>
          Abmelden
        </Button>
      }
    />
  );
};

/**
 * Route-Schutz: verlangt Anmeldung und optional eine Dienst-Berechtigung.
 * Ohne Berechtigung wird auf die Startseite des Benutzers umgeleitet.
 */
const PrivateRoute = ({ children, permission }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return <div style={{ padding: '24px' }}>Wird geladen…</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (permission && !hasPermission(permission)) {
    // Startseite je nach Berechtigung; ganz ohne Suche-Recht: Hinweis anzeigen
    if (permission !== 'suche' && hasPermission('suche')) {
      return <Navigate to={hasPermission('ki_suche') ? '/' : '/search'} />;
    }
    return <AccessDenied />;
  }

  return children;
};

/** Startseite: KI-Suche, oder klassische Suche für Benutzer ohne KI-Zugriff. */
const Home = () => {
  const { hasPermission } = useAuth();
  return hasPermission('ki_suche') ? <AgentChat /> : <Navigate to="/search" />;
};

const App = () => {
  return (
    <ConfigProvider
      locale={deDE}
      theme={{
        token: {
          colorPrimary: '#b6ce3b',
          colorInfo: '#b6ce3b',
          borderRadius: 8,
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          colorText: '#1c211a',
          colorTextSecondary: '#5d6457',
          colorTextTertiary: '#8f968a',
          colorBorder: '#d5dac6',
          colorBorderSecondary: '#e6e9dc',
          colorBgLayout: '#f6f7f1',
          colorLink: '#5f7d16',
          colorLinkHover: '#4c650f',
          controlOutline: 'rgba(182, 206, 59, 0.32)',
          fontSize: 14,
        },
        components: {
          Button: {
            primaryColor: '#12160f',
            fontWeight: 500,
          },
          Layout: {
            headerBg: '#ffffff',
            siderBg: '#ffffff',
            bodyBg: '#f6f7f1',
          },
          Table: {
            headerBg: '#f4f6ec',
            headerColor: '#5d6457',
            rowHoverBg: '#f3f7e2',
          },
          Card: {
            headerFontSize: 15,
          },
        },
      }}
    >
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/search"
              element={
                <PrivateRoute permission="suche">
                  <SearchInterface />
                </PrivateRoute>
              }
            />
            <Route
              path="/estimate"
              element={
                <PrivateRoute permission="kostenschaetzung">
                  <CostEstimation />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute permission="benutzerverwaltung">
                  <UserAdmin />
                </PrivateRoute>
              }
            />
            <Route path="/agentic-search" element={<Navigate to="/" />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
